/**
 * Parse a date string from the API as UTC
 * @param dateString - ISO date string (assumed to be in UTC if no timezone specified)
 * @returns Date object
 */
export const parseUTCDate = (dateString: string): Date => {
  // Always parse as UTC by adding 'Z' if not present
  return new Date(dateString + (dateString.endsWith('Z') ? '' : 'Z'));
};

/**
 * Format a date string to show the full date, time, and timezone in the user's local timezone
 * @param dateString - ISO date string (assumed to be in UTC if no timezone specified)
 * @returns Formatted string with date, time, and timezone
 */
export const formatDueDateWithTimezone = (dateString: string): string => {
  // Parse the date string as UTC if no timezone is specified
  const date = parseUTCDate(dateString);

  // Get timezone name
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'long'
  });

  const parts = formatter.formatToParts(date);
  const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || timeZone;

  // Format the full date with time and day of week
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  };

  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

  return `${formattedDate} ${timeZoneName}`;
};

/**
 * Check if a date is overdue
 * @param dateString - ISO date string (assumed to be UTC if no timezone specified)
 * @returns true if the date is in the past
 */
export const isOverdue = (dateString: string): boolean => {
  // Parse the date string as UTC if no timezone is specified
  // This is the standard way to handle UTC dates from APIs
  const date = parseUTCDate(dateString);
  return date < new Date();
};